/*eslint max-lines-per-function: ["error", 190]*/
import { Buffer } from 'buffer';

import { saveAs } from 'file-saver';
import mime from 'mime';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import './DragNDropFiles.css';
import * as Icon from 'react-bootstrap-icons';

import { useSaveFileInBrowser } from '../../hooks/useSaveFileInBrowser';

import DragNDropFileList from './DragNDropFileList';

const downloadFileFromMemory = (file: NewFile) => {
  const blob = createBlobFromMemory(file);
  saveAs(blob, file.name);
};

const createBlobFromMemory = (file: NewFile) => {
  const mimeType = mime.getType(file.name) || undefined;
  return new Blob(
    [new Uint8Array(file.content)],
    mimeType ? { type: mimeType } : undefined,
  );
};

export type DragNDropFile = {
  id: string;
  name: string;
  size: number;
};

export type NewFile = {
  id: string;
  name: string;
  content: Buffer;
};

interface DragNDropFilesProps {
  existingFiles?: DragNDropFile[];
  setFilesToAdd: React.Dispatch<React.SetStateAction<NewFile[]>>;
  setFilesToDelete?: React.Dispatch<React.SetStateAction<string[]>>;
  setMaxFileSizeExceeded?: React.Dispatch<React.SetStateAction<boolean>>;
  maxFileSize?: number;
  onChange?: () => void;
  readOnly?: boolean;
}

const DragNDropFiles: React.FC<DragNDropFilesProps> = props => {
  const saveFileInBrowser = useSaveFileInBrowser();
  const maxFileSize = props.maxFileSize || 25 * 1024 * 1024;
  const [files, setFiles] = useState<DragNDropFile[]>(
    props.existingFiles || [],
  );
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [newlyAddedFiles, setNewlyAddedFiles] = useState<NewFile[]>([]);
  const prevExistingFilesRef = useRef<string>('');

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const newFiles = acceptedFiles.map(file => ({
        id: Math.random().toString(36).substr(2, 9),
        name: file.name,
        size: file.size,
      }));
      setFiles(prev => [...prev, ...newFiles]);
      acceptedFiles.forEach(fileObj => {
        const reader = new FileReader();
        reader.onload = () => {
          const fileContent = reader.result;
          const attachment: NewFile = {
            id: newFiles.find(f => f.name === fileObj.name)?.id || '',
            name: fileObj.name,
            content: Buffer.from(fileContent as ArrayBuffer),
          };
          props.setFilesToAdd(prev => [...prev, attachment]);
          setNewlyAddedFiles(prev => [...prev, attachment]);
        };
        reader.readAsArrayBuffer(fileObj);
      });
      props.onChange?.();
    },
    [props],
  );

  useEffect(() => {
    const totalSize = files.reduce((acc, file) => acc + Number(file.size), 0);
    if (totalSize > maxFileSize) {
      setShowSaveErrors(true);
      setSaveErrors([
        'Łączny rozmiar plików przekracza dozwolony limit {{maxSize}}.'.replace(
          '{{maxSize}}',
          formatFileSize(maxFileSize),
        ),
      ]);
      props.setMaxFileSizeExceeded?.(true);
    } else {
      setShowSaveErrors(false);
      setSaveErrors([]);
      props.setMaxFileSizeExceeded?.(false);
    }
  }, [files, maxFileSize, props]);

  useEffect(() => {
    const existingIds =
      props.existingFiles
        ?.map(f => f.id)
        .sort()
        .join(',') || '';

    if (existingIds && existingIds !== prevExistingFilesRef.current) {
      prevExistingFilesRef.current = existingIds;
      setFiles(props.existingFiles || []);
      setNewlyAddedFiles([]);
    }
  }, [props.existingFiles]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    disabled: props.readOnly,
  });

  const removeFile = (fileId: string) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
    if (props.existingFiles?.find(f => f.id === fileId)) {
      props.setFilesToDelete?.(prev => [...(prev || []), fileId]);
    } else {
      props.setFilesToAdd(prev => prev.filter(f => f.id !== fileId));
      setNewlyAddedFiles(prev => prev.filter(f => f.id !== fileId));
    }
    props.onChange?.();
  };

  const downloadFile = (fileId: string, fileName: string) => {
    const newFile = newlyAddedFiles.find(f => f.id === fileId);
    if (newFile) {
      downloadFileFromMemory(newFile);
    } else {
      saveFileInBrowser(fileId, fileName, 'download');
    }
  };

  const openFile = (fileId: string, fileName: string) => {
    const newFile = newlyAddedFiles.find(f => f.id === fileId);
    if (newFile) {
      const blob = createBlobFromMemory(newFile);
      window.open(URL.createObjectURL(blob), '_blank', 'noopener,noreferrer');
    } else {
      saveFileInBrowser(fileId, fileName, 'open');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) {
      return '0 Bytes';
    }
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
  return (
    <div className="file-upload-container">
      {!props.readOnly && (
        <div
          {...getRootProps()}
          className={`dropzone ${isDragActive ? 'dropzone-active' : ''}`}
        >
          <input {...getInputProps()} />

          <div className="dropzone-content">
            <Icon.Upload
              className={`upload-icon ${isDragActive ? 'text-blue-500' : 'text-gray-400'}`}
            />
            <div>
              <p className="dropzone-text">
                {isDragActive
                  ? 'Przeciągnij i upuść pliki tutaj'
                  : 'Kliknij, aby wybrać pliki lub przeciągnij je tutaj'}
              </p>
              <p className="dropzone-subtext">{'Maks 25 MB.'}</p>
            </div>
          </div>
        </div>
      )}

      {files.length > 0 && (
        <DragNDropFileList
          files={files}
          readOnly={props.readOnly}
          showSaveErrors={showSaveErrors}
          saveErrors={saveErrors}
          formatFileSize={formatFileSize}
          onOpenFile={openFile}
          onDownloadFile={downloadFile}
          onRemoveFile={removeFile}
        />
      )}
    </div>
  );
};

export default DragNDropFiles;
